import { type Signal, type ReadonlySignal } from '@preact/signals-react'

import { type EApplicationStatus } from './applications'
import type { IMenuItem } from './menuItem'
import type { IRole } from './roles'

export const enum EAuthModalTypes {
  AUTH_PERSONAL_DATA = 'authPersonalData',
  TERMS_AND_CONDITIONS = 'termsAndConditions',
}

export const enum EUserPermissionType {
  ALL = 'all',
  NO = 'no',
  TEAM = 'team',
  YES = 'yes',
}

export interface TAuthModal {
  data: null | {
    text: string
    title: string
  }
  open: boolean
  type: null | string
}
export interface TForgotPwdData {
  email: string
}

export interface TSignupData {
  authPersonalData: boolean
  authTermsAndConditions: boolean
  birthdate: string
  confirmEmail?: string
  confirmPassword?: string
  email: string
  firstName: string
  firstSurname: string
  identificationNumber: string
  identificationType: string
  password: string
  secondName?: string
  secondSurname?: string
}

export interface TRole {
  id?: string
  name?: string
}

export interface TSignIn {
  altcha?: string | null
  password: string
  username: string
}

export interface TAuthCurrentUserState {
  birthdate?: string
  currentRole?: IRole | string | null
  currentRoleId?: number | null
  firstName?: string
  id?: string
  isActive?: number
  lastName?: string
  permission?: TAuthUserPermissionResponse[]
  role?: TRole | null
  salutationName?: string
  statusPermissions?: EApplicationStatus[]
  userName?: string
}

export interface TAuthUserPermissionResponse {
  entity: string
  permission: {
    create: EUserPermissionType
    delete: EUserPermissionType
    edit: EUserPermissionType
    read: EUserPermissionType
    stream: EUserPermissionType
  }
}

export interface TAuthUserPermission {
  create: boolean
  delete: boolean
  edit: boolean
  read: boolean
  stream?: boolean
}

export interface TAuthState {
  applicationPermission: ReadonlySignal<TAuthUserPermission>
  authModal: Signal<TAuthModal>
  currentRole: Signal<TRole | null>
  currentUser: Signal<TAuthCurrentUserState>
  isLoggedIn: ReadonlySignal<boolean>
  menu: Signal<IMenuItem[]>
  permissions: Signal<string[]>
  resetPwdCode: Signal<string | null>
  roles: Signal<IRole[]>
  routes: ReadonlySignal<string[]>
  statusChangeAllowed: ReadonlySignal<EApplicationStatus[]>
  token: Signal<string | null>
  userPermissions: Signal<Record<string, TAuthUserPermission>>
  userRefresh: Signal<string | null>
}

/**
 * Interface representing login values.
 * - password: Password for login.
 * - username: Username for login.
 */
export interface LoginValues {
  email: string
  password: string
}

/**
 * Interface representing a status.
 * - code: Status code.
 * - message: Status message.
 */
interface Status {
  code: number
  message: string
}

/**
 * Interface representing a user.
 * - email: User's email address.
 * - full name: User's full name.
 * - isAdmin: Indicates whether the user is an administrator.
 * - role: User's role.
 * - username: User's username.
 */
export interface User {
  email: string
  fullName: string
  isAdmin: boolean
  role: string
  username: string
}

/**
 * Interface representing a data item.
 * - expireAt: Expiration time in milliseconds.
 * - token: Authentication token.
 * - user: Associated user data.
 */
interface DataItem {
  expireAt: number
  token: string
  user: User
}

/**
 * Interface representing an authentication response.
 * - data: Array of data items.
 * - status: Authentication status.
 */
export interface AuthResponse {
  data: DataItem
  status: Status
}
